/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/api';
import { API } from '../utils/api';
import { ArrowLeft, Search, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import TopHeader from '../components/common/TopHeader';
import BottomNav from '../components/common/BottomNav';
import ActionCard from '../components/cards/ActionCard';
import { getCategory } from '../utils/homeHelpers';

const SCOPE_OPTIONS = [
  { value: 'solo', label: 'Solo', color: '#2C6A5F', bg: '#B4E1D8' },
  { value: 'team', label: 'Team', color: '#1A4A42', bg: '#73C8B9' },
  { value: 'company', label: 'Company', color: '#7A4420', bg: '#F8AD7B' },
  { value: 'community', label: 'Community', color: '#9D174D', bg: '#FBCFE8' },
];

/**
 * Full-page browser for the Action Library. Structure mirrors the
 * community `/community/events` page: back button + title, search,
 * tab (All / My), scope filter chips, then a vertical list of the
 * existing org-side ActionCard.
 */
const AllActionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isHRAdmin = user?.role === 'HR_ADMIN';

  const [allChallenges, setAllChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [joinedActions, setJoinedActions] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const [showMyMAD, setShowMyMAD] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [allRes, myRes] = await Promise.all([
          axios.get(`${API}/challenges?active_only=true`).catch(() => ({ data: [] })),
          axios.get(`${API}/challenges/my?sort=event_date`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setAllChallenges(allRes.data || []);
        setMyChallenges(myRes.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const myJoinedChallengeIds = useMemo(() => new Set(myChallenges.map(c => c.id)), [myChallenges]);
  const isJoined = (challenge) => {
    if (!challenge) return false;
    if (joinedActions.has(challenge.id)) return true;
    if (myJoinedChallengeIds.has(challenge.id)) return true;
    if (!user?.id) return false;
    const participants = challenge.participants || [];
    return participants.some(p => (typeof p === 'string' ? p : p?.user_id) === user.id);
  };
  const isExpired = (challenge) => {
    if (!challenge?.end_date) return false;
    return new Date(challenge.end_date) < new Date();
  };

  const handleJoin = async (challengeId) => {
    setJoinedActions(prev => new Set(prev).add(challengeId));
    try {
      await axios.post(`${API}/challenges/${challengeId}/join`);
      // Refresh my-challenges list so the "Joined" state persists after
      // this session.
      const my = await axios.get(`${API}/challenges/my?sort=event_date`).catch(() => ({ data: [] }));
      setMyChallenges(my.data || []);
    } catch (err) {
      setJoinedActions(prev => { const s = new Set(prev); s.delete(challengeId); return s; });
      alert(err.response?.data?.detail || 'Failed to join');
    }
  };

  const filtered = useMemo(() => {
    const universe = showMyMAD ? myChallenges : allChallenges;
    return universe.filter(challenge => {
      if (scopeFilter) {
        const modeMap = { solo: ['solo', 'individual'], team: ['team'], company: ['company'], community: ['community'] };
        if (!modeMap[scopeFilter]?.includes(challenge.mode?.toLowerCase())) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const title = (challenge.title || '').toLowerCase();
        const desc = (challenge.description || '').toLowerCase();
        const loc = (challenge.location || '').toLowerCase();
        const cat = (getCategory(challenge.title, challenge.category) || '').toLowerCase();
        if (!title.includes(q) && !desc.includes(q) && !loc.includes(q) && !cat.includes(q)) return false;
      }
      return true;
    });
  }, [allChallenges, myChallenges, showMyMAD, scopeFilter, searchQuery]);

  return (
    <div style={{ background: '#F5F7FA', minHeight: '100vh', paddingBottom: 80 }}>
      <TopHeader />
      <div style={{ width: '100%', maxWidth: 720, margin: '0 auto', padding: '0 16px' }}>
        {/* Back */}
        <button type="button" onClick={() => navigate('/')}
          data-testid="all-actions-back-btn"
          style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 6, color: '#6B7280', fontSize: 13, fontWeight: 500, cursor: 'pointer', margin: '12px 0 4px', padding: 0 }}>
          <ArrowLeft size={14} /> Back
        </button>

        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1F2937', margin: '6px 0 14px' }}>All Actions</h1>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search Actions..."
            data-testid="all-actions-search"
            style={{
              width: '100%', padding: '11px 36px 11px 36px', borderRadius: 12,
              border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none',
              background: '#FFFFFF', boxSizing: 'border-box',
            }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} data-testid="all-actions-clear-search"
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 2 }}>
              <X size={16} />
            </button>
          )}
        </div>

        {/* All Action / My Action tabs — moved here from the WE page. */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, background: '#F1F3F5', padding: 3, borderRadius: 8 }}>
          <button data-testid="all-actions-tab-all"
            onClick={() => setShowMyMAD(false)}
            style={{
              flex: 1, padding: '8px', background: !showMyMAD ? 'white' : 'transparent',
              border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
              color: !showMyMAD ? '#1F2937' : '#6B7280', cursor: 'pointer',
              transition: 'all 0.2s', boxShadow: !showMyMAD ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
            }}>
            All Action
          </button>
          <button data-testid="all-actions-tab-my"
            onClick={() => setShowMyMAD(true)}
            style={{
              flex: 1, padding: '8px', background: showMyMAD ? 'white' : 'transparent',
              border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
              color: showMyMAD ? '#1F2937' : '#6B7280', cursor: 'pointer',
              transition: 'all 0.2s', boxShadow: showMyMAD ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
            }}>
            My Action
          </button>
        </div>

        {/* Scope filter chips — moved here from the WE page. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {SCOPE_OPTIONS.map(scope => (
            <button key={scope.value} data-testid={`all-actions-scope-${scope.value}`}
              onClick={() => setScopeFilter(prev => prev === scope.value ? null : scope.value)}
              style={{
                padding: '6px 14px',
                background: scopeFilter === scope.value ? scope.bg : '#FFFFFF',
                color: scopeFilter === scope.value ? scope.color : '#6B7280',
                border: `1px solid ${scopeFilter === scope.value ? scope.color + '40' : '#E5E7EB'}`,
                borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s ease'
              }}>
              {scope.label}
            </button>
          ))}
        </div>

        {/* Actions list — vertical stack of the same org-side ActionCard. */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 13 }}>Loading actions…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 13 }} data-testid="all-actions-empty">
            {showMyMAD ? 'No saved actions' : searchQuery ? 'No matching actions' : 'No actions match your filters.'}
          </div>
        ) : (
          <div data-testid="all-actions-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filtered.map(challenge => (
              <ActionCard
                key={challenge.id}
                challenge={challenge}
                isJoined={isJoined(challenge)}
                isExpired={isExpired(challenge)}
                onJoin={handleJoin}
                onOpen={() => navigate(`/challenges/${challenge.id}`)}
                isHRAdmin={isHRAdmin}
              />
            ))}
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default AllActionsPage;
